import { memo, useMemo } from 'react';

import type { KcContext as KeycloakifyKcContext } from 'keycloakify/login/KcContext';

import { Body } from '@tripleten/design-system/body';
import { Theme } from '@tripleten/design-system/theme';
import { useBooleanState } from '@tripleten/state-hooks/use-boolean-state';

import { MessageToast } from 'src/shared/components/message-toast/message-toast.js';
import type { KcContextWithCustomPages } from 'src/shared/utils/kc-context/kc-context.js';

import { Error } from 'src/pages/error/error.js';
import { LoginConfigTotp } from 'src/pages/login-config-totp/login-config-totp.js';
import { LoginOtp } from 'src/pages/login-otp/login-otp.js';
import { LoginResetPassword } from 'src/pages/login-reset-password/login-reset-password.js';
import { LoginUpdatePassword } from 'src/pages/login-update-password/login-update-password.js';
import { LoginUpdateProfile } from 'src/pages/login-update-profile/login-update-profile.js';
import { LoginUsername } from 'src/pages/login-username/login-username.js';
import { LoginVerifyEmail } from 'src/pages/login-verify-email/login-verify-email.js';
import { Login } from 'src/pages/login/login.js';
import { MagicLinkConfirm } from 'src/pages/magic-link-confirm/magic-link-confirm.js';
import { MagicLinkForm } from 'src/pages/magic-link-form/magic-link-form.js';
import { PageInternalLogin } from 'src/pages/page-internal-login/page-internal-login.js';
import { PageInternalResetPassword } from 'src/pages/page-internal-reset-password/page-internal-reset-password.js';
import { PageInternalUpdatePassword } from 'src/pages/page-internal-update-password/page-internal-update-password.js';
import { Register } from 'src/pages/register/register.js';
import { SamlPostForm } from 'src/pages/saml-post-form/saml-post-form.js';

import { DefaultKeycloakPage } from 'src/keycloak-theme/login/default-page.js';

import { Header } from './header/header.js';
import { cnKcApp } from './kc-app.constants.js';

import './kc-app.css';

const PAGES_WITH_VISIBLE_MESSAGE = ['login.ftl', 'register.ftl', 'login-update-password.ftl'];

export interface KcAppProps {
    kcContext: KcContextWithCustomPages;
}

export const KcApp = memo(({ kcContext }: KcAppProps) => {
    const { state: isToastVisible, setFalse: closeToast } = useBooleanState(true);

    const KeycloakComponent = useMemo(() => {
        switch (kcContext.pageId) {
            case 'login.ftl':
                return kcContext.realm.displayName === 'internal' ? (
                    <PageInternalLogin kcContext={kcContext} />
                ) : (
                    <Login kcContext={kcContext} />
                );
            case 'info.ftl':
            case 'error.ftl':
            case 'login-page-expired.ftl':
                return <Error kcContext={kcContext} />;
            case 'register.ftl':
                return <Register kcContext={kcContext} />;
            case 'login-verify-email.ftl':
                return <LoginVerifyEmail kcContext={kcContext} />;
            case 'login-reset-password.ftl':
                return kcContext.realm.displayName === 'internal' ? (
                    <PageInternalResetPassword kcContext={kcContext} />
                ) : (
                    <LoginResetPassword kcContext={kcContext} />
                );

            case 'login-update-password.ftl':
                return kcContext.realm.displayName === 'internal' ? (
                    <PageInternalUpdatePassword kcContext={kcContext} />
                ) : (
                    <LoginUpdatePassword kcContext={kcContext} />
                );
            case 'login-update-profile.ftl':
                return <LoginUpdateProfile kcContext={kcContext} />;
            case 'login-username.ftl':
                return <LoginUsername kcContext={kcContext} />;
            case 'magic-link-form.ftl':
                return <MagicLinkForm kcContext={kcContext} />;
            case 'magic-link-confirm.ftl':
                return <MagicLinkConfirm kcContext={kcContext} />;

            case 'login-otp.ftl':
                return <LoginOtp kcContext={kcContext} />;
            case 'login-config-totp.ftl':
                return <LoginConfigTotp kcContext={kcContext} />;

            case 'saml-post-form.ftl':
                return <SamlPostForm kcContext={kcContext} />;

            default:
                // Render Keycloak's default page when no custom component is provided
                return <DefaultKeycloakPage kcContext={kcContext as KeycloakifyKcContext} />;
        }
    }, [kcContext]);

    const { message } = kcContext;
    const isPageWithVisibleMessage = PAGES_WITH_VISIBLE_MESSAGE.includes(kcContext.pageId);
    const isMessageToastVisible = isToastVisible && isPageWithVisibleMessage && message?.summary;

    return (
        <Theme appearance="light" asChild>
            <Body>
                <div className={cnKcApp('main')}>
                    <Header logoType={kcContext.pageId === 'error.ftl' ? 'outlined' : 'default'} />
                    <div className={cnKcApp('page')}>{KeycloakComponent}</div>
                    {isMessageToastVisible && <MessageToast message={message.summary} onClose={closeToast} />}
                </div>
            </Body>
        </Theme>
    );
});
